import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { EQUAL, Filter, IS_EMPTY, IS_NOT_EMPTY, Operator, Record } from "@dto/records";
import { getRecords } from "@utils/data";
import { getFieldKey, getOperatorLabel } from "@utils/model";
import { TW_FilterEditorOperatorSelect, TW_FilterEditorOperatorSelectFull, TW_FilterEditorValue_Wrapper } from "@utils/twClasses";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore } from "src/store";
import { PrimitiveSelect } from "./PrimiteSelect";
import { RecordSelect } from "./RecordSelect";
import { buildLinkedTextFilter, buildRecordKeyFilter, isTextMatchOperator } from "./recordFilterLogic";

interface RecordFilterValueSelectorProps {
  selectedPathKey: string | undefined;
  filter: Filter;
  validOperators: Operator[];
  validateFilter: (filter: Filter) => void;
  disabled: boolean
}

export function RecordFilterValueSelector({ selectedPathKey, filter, validOperators, validateFilter, disabled }: RecordFilterValueSelectorProps) {

  const { t } = useTranslation();
  const workspace = useModelStore((state) => state.selectedWorkspace);
  const [selectedOperator, setSelectedOperator] = useState<Operator | undefined>(EQUAL);
  const [selectedRecordKeys, setSelectedRecordKeys] = useState<string[] | undefined>(undefined);
  const [selectedText, setSelectedText] = useState<any>(undefined);
  const [availableRecords, setAvailableRecords] = useState<Record[] | undefined>(undefined);
  const fieldByKeys = useModelStore((state) => state.fieldByKeys);

  useEffect(() => {
    if (filter) {
      const fieldFilter = filter.groupFilter;
      const filters = fieldFilter?.filters;
      if (filters && filters.length > 0) {
        const firstFilter = filters[0];
        const valueKeys = filters.map(filter => filter?.singleFilter?.valueKey).filter(valueKey => valueKey !== null) as string[];
        setSelectedOperator(firstFilter?.singleFilter?.operator);
        setSelectedRecordKeys(valueKeys);
        setSelectedText(firstFilter?.singleFilter?.value ?? undefined);
      }
    }
  }, [filter]);

  useEffect(() => {
    if (!selectedPathKey || !workspace?._key) {
      return;
    }

    const fieldKey = getFieldKey(selectedPathKey);
    if (!fieldKey) {
      return;
    }

    const field = fieldByKeys?.get(fieldKey.fieldKey);
    if (!field) {
      return;
    }

    getRecords(workspace._key, fieldKey.objectKey, [], [fieldKey.fieldKey], null).then((response) => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            console.error(translate(t, cError.i18n, cError.params));
          } else {
            console.error(cError.message);
          }
        } else {
          setAvailableRecords(response as Record[]);
        }
      } else {
        setAvailableRecords([]);
      }
    })
    return;

  }, [selectedPathKey]);

  const onOperatorChange = (operator: Operator) => {
    setSelectedOperator(operator);

    if (!selectedPathKey || !operator) {
      return;
    }

    if (operator === IS_EMPTY || operator === IS_NOT_EMPTY) {
      validateFilter(buildRecordKeyFilter(selectedPathKey, operator, []));
    } else if (isTextMatchOperator(operator)) {
      // Text-match operators filter on the destination field text value: only
      // emit once a text has been typed.
      if (selectedText) {
        validateFilter(buildLinkedTextFilter(selectedPathKey, operator, selectedText));
      }
    } else if (selectedRecordKeys && selectedRecordKeys.length > 0) {
      validateFilter(buildRecordKeyFilter(selectedPathKey, operator, selectedRecordKeys));
    }
  }

  const onValueChange = (records: Record[]) => {
    const newSelectedRecordKeys = records.map(record => record._key);
    setSelectedRecordKeys(newSelectedRecordKeys);
    if (selectedPathKey && selectedOperator) {
      validateFilter(buildRecordKeyFilter(selectedPathKey, selectedOperator, newSelectedRecordKeys));
    }
  }

  const onTextChange = (value: any) => {
    setSelectedText(value);
    if (value === undefined) {
      return;
    }
    if (selectedPathKey && selectedOperator && isTextMatchOperator(selectedOperator)) {
      validateFilter(buildLinkedTextFilter(selectedPathKey, selectedOperator, value));
    }
  }

  const selectedRecords = availableRecords?.filter(record => selectedRecordKeys?.includes(record._key));

  const fieldKey = selectedPathKey ? getFieldKey(selectedPathKey) : null;
  const field = fieldKey ? (fieldByKeys?.get(fieldKey.fieldKey) || null) : null;

  const hasValueInput = selectedOperator !== IS_EMPTY && selectedOperator !== IS_NOT_EMPTY;
  const isTextMatch = isTextMatchOperator(selectedOperator);
  const disabledOperator = false;
  return (
    <div className={TW_FilterEditorValue_Wrapper}>
      <Select onValueChange={onOperatorChange} value={selectedOperator} disabled={disabled}>
        <SelectTrigger className={(selectedOperator === IS_EMPTY || selectedOperator === IS_NOT_EMPTY) ? TW_FilterEditorOperatorSelectFull : TW_FilterEditorOperatorSelect} disabled={disabledOperator}>
          <SelectValue placeholder={t("filter.operator.placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {validOperators.map((operator) => {
            return (
              <SelectItem key={operator} value={operator}>{getOperatorLabel(operator, t)}</SelectItem>
            )
          })}
        </SelectContent>
      </Select>
      {hasValueInput && !isTextMatch &&
        <RecordSelect pathKey={selectedPathKey ?? null} field={field} allRecords={availableRecords || []} selectedRecords={selectedRecords || []} disabled={disabledOperator || disabled} onSelectionChange={onValueChange} />
      }
      {hasValueInput && isTextMatch &&
        <PrimitiveSelect pathKey={selectedPathKey ?? null} field={field} value={selectedText} disabled={disabledOperator || disabled} onValueChange={onTextChange} />
      }
    </div>
  )
}
